import { z } from "zod";
import {
  SCHEDULE_FREQUENCIES,
  SYNC_JOB_KINDS,
  SYNC_JOB_STATUSES,
  SYNC_RUN_KINDS,
  SYNC_RUN_SKIP_REASONS,
  SYNC_RUN_STATUSES,
  SYNC_RUN_TRIGGERS,
  ZVOOVE_MODES,
} from "../enums.js";
import { idSchema, isoDateSchema, isoTimestampSchema } from "./common.js";

export const syncRunSchema = z.object({
  id: z.string(),
  kind: z.enum(SYNC_RUN_KINDS),
  trigger: z.enum(SYNC_RUN_TRIGGERS),
  status: z.enum(SYNC_RUN_STATUSES),
  startedAt: isoTimestampSchema,
  finishedAt: isoTimestampSchema.nullable(),
  calls: z.number().int(),
  summary: z.record(z.string(), z.number()).nullable(),
  error: z.string().nullable(),
  // Why a `skipped` run did not start (extension 1: the call budget); null otherwise.
  skipReason: z.enum(SYNC_RUN_SKIP_REASONS).nullable(),
  // `ok` but cut short by the call budget (`summary.budget_truncated`): the next run resumes.
  partial: z.boolean(),
});
export type SyncRun = z.infer<typeof syncRunSchema>;

// --- schedules and the monthly call budget (extension 1) ---------------------------------------

export const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// `settings.schedule_<kind>`: how often a kind runs and, for daily/weekly/monthly, at which
// Berlin time (weekly = Sunday, monthly = the 1st).
export const scheduleSettingSchema = z.object({
  frequency: z.enum(SCHEDULE_FREQUENCIES),
  time: z.string().regex(TIME_OF_DAY_PATTERN, "HH:mm"),
});
export type ScheduleSetting = z.infer<typeof scheduleSettingSchema>;

export const schedulesSchema = z.object({
  master_data: scheduleSettingSchema,
  assignments: scheduleSettingSchema,
  full: scheduleSettingSchema,
  outbound: scheduleSettingSchema,
  readback: scheduleSettingSchema,
  retention: scheduleSettingSchema,
});
export type Schedules = z.infer<typeof schedulesSchema>;

export const BUDGET_LEVELS = ["ok", "warning", "inbound_stop", "exhausted"] as const;
export type BudgetLevel = (typeof BUDGET_LEVELS)[number];

// Month accounting of the zvoove calls: what was used, what is held back for outbound jobs and
// manual runs, and the linear projection to the end of the month.
export const callBudgetSchema = z.object({
  // Berlin calendar month, `YYYY-MM`.
  month: z.string(),
  callsThisMonth: z.number().int(),
  budgetThisMonth: z.number().int(),
  reservePercent: z.number().int(),
  reservedForOutbound: z.number().int(),
  // Scheduled inbound runs stop here: budget − reserve.
  inboundStop: z.number().int(),
  projectedMonthEnd: z.number().int(),
  // Derived display value: budget ÷ days of the month.
  callBudgetPerDay: z.number().int(),
  daysElapsed: z.number().int(),
  daysInMonth: z.number().int(),
  // `warning` from 60 %, `inbound_stop` from the inbound stop, `exhausted` at 100 %.
  level: z.enum(BUDGET_LEVELS),
});
export type CallBudget = z.infer<typeof callBudgetSchema>;

export const syncKindOverviewSchema = z.object({
  kind: z.enum(SYNC_RUN_KINDS),
  implemented: z.boolean(),
  schedule: scheduleSettingSchema,
  // Calls the next run of this kind is expected to make (0 = nothing due).
  estimatedCalls: z.number().int(),
  lastRun: syncRunSchema.nullable(),
  lastOk: syncRunSchema.nullable(),
  running: z.boolean(),
});
export type SyncKindOverview = z.infer<typeof syncKindOverviewSchema>;

export const syncOverviewSchema = z.object({
  mode: z.enum(ZVOOVE_MODES),
  writeAllowedEmployeeIds: z.union([z.array(z.number().int()), z.literal("all")]),
  schedulerEnabled: z.boolean(),
  callsToday: z.number().int(),
  kinds: z.array(syncKindOverviewSchema),
  recentRuns: z.array(syncRunSchema),
  // Pause reason (`auth` after 401/403) or null; `pausedUntil` is the 15 min pause after 429.
  paused: z.string().nullable(),
  pausedError: z.string().nullable(),
  pausedUntil: isoTimestampSchema.nullable(),
  budget: callBudgetSchema,
  queuedJobs: z.number().int(),
  failedJobs: z.number().int(),
});
export type SyncOverview = z.infer<typeof syncOverviewSchema>;

// --- outbound jobs (B1) -----------------------------------------------------------------------

export const SYNC_ENTITY_KINDS = ["time_entry", "absence_request"] as const;
export type SyncEntityKind = (typeof SYNC_ENTITY_KINDS)[number];

// What a job carries, enough for the UI to link to the dispatcher view of the entity.
export const syncJobEntitySchema = z.object({
  kind: z.enum(SYNC_ENTITY_KINDS),
  id: z.string(),
  // Time entries: work date; absences: first day of the range.
  date: isoDateSchema.nullable(),
  // Live row (not superseded / still open) — a job of a corrected row is informational only.
  live: z.boolean(),
});
export type SyncJobEntity = z.infer<typeof syncJobEntitySchema>;

export const syncJobSchema = z.object({
  id: z.string(),
  kind: z.enum(SYNC_JOB_KINDS),
  status: z.enum(SYNC_JOB_STATUSES),
  attempts: z.number().int(),
  nextAttemptAt: isoTimestampSchema.nullable(),
  lastError: z.string().nullable(),
  createdAt: isoTimestampSchema,
  startedAt: isoTimestampSchema.nullable(),
  finishedAt: isoTimestampSchema.nullable(),
  readBackAt: isoTimestampSchema.nullable(),
  zvEmployeeId: z.number().int().nullable(),
  employeeName: z.string().nullable(),
  entities: z.array(syncJobEntitySchema),
});
export type SyncJob = z.infer<typeof syncJobSchema>;

export const syncJobsQuerySchema = z.object({
  status: z.enum(SYNC_JOB_STATUSES).optional(),
  kind: z.enum(SYNC_JOB_KINDS).optional(),
  // Only jobs created on or after this instant …
  since: isoTimestampSchema.optional(),
  // … or within the last n days, evaluated with the api clock (the failed list shows 7 days).
  lastDays: z.coerce.number().int().min(1).max(365).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type SyncJobsQuery = z.infer<typeof syncJobsQuerySchema>;

export const syncJobsResponseSchema = z.object({ jobs: z.array(syncJobSchema) });
export type SyncJobsResponse = z.infer<typeof syncJobsResponseSchema>;

export const syncJobIdParamsSchema = z.object({ id: idSchema });

export const syncEntityParamsSchema = z.object({ entity: z.enum(SYNC_ENTITY_KINDS), id: idSchema });

// One thing a dispatcher has to look at: a failed job or an entity zvoove rejected on read-back.
export const syncProblemSchema = z.object({
  entity: z.enum(SYNC_ENTITY_KINDS),
  entityId: z.string(),
  zvEmployeeId: z.number().int(),
  employeeName: z.string(),
  date: isoDateSchema.nullable(),
  error: z.string(),
  // The failed job behind it, null for a read-back rejection (a correction re-enqueues).
  jobId: z.string().nullable(),
  retryable: z.boolean(),
  at: isoTimestampSchema,
});
export type SyncProblem = z.infer<typeof syncProblemSchema>;

export const retryEntityResponseSchema = z.object({ retried: z.number().int() });
export type RetryEntityResponse = z.infer<typeof retryEntityResponseSchema>;

export const syncRunKindParamsSchema = z.object({ kind: z.enum(SYNC_RUN_KINDS) });

export const syncRunsQuerySchema = z.object({
  kind: z.enum(SYNC_RUN_KINDS).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type SyncRunsQuery = z.infer<typeof syncRunsQuerySchema>;

export const syncRunsResponseSchema = z.object({ runs: z.array(syncRunSchema) });
export type SyncRunsResponse = z.infer<typeof syncRunsResponseSchema>;
